// src/features/services/sales/components/WorkOrderList.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Compact list of production / work orders with status badges and actions.
// PAC Glass surfaces. Data owned by useProductionOrders hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useProductionOrders } from '../hooks/useProductionOrders';
import type { WorkOrder, CompleteWorkOrderPayload } from '../api/productionOrderApi';

const STATUS_COLORS: Record<string, string> = {
  draft:        'bg-neutral-800 text-neutral-400 border-neutral-700/40',
  in_progress:  'bg-blue-900/40 text-blue-400 border-blue-700/40',
  completed:    'bg-emerald-900/40 text-emerald-400 border-emerald-700/40',
  cancelled:    'bg-rose-900/40 text-rose-400 border-rose-700/40',
};

const STATUS_LABELS: Record<string, string> = {
  draft:        'draft',
  in_progress:  'in progress',
  completed:    'completed',
  cancelled:    'cancelled',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] ?? 'bg-neutral-800 text-neutral-400 border-neutral-700/40';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ── Detail / Action panel ────────────────────────────────────────────────────

function WorkOrderDetail({ wo }: { wo: WorkOrder }) {
  const { start, complete, cancel } = useProductionOrders();
  const [showComplete, setShowComplete] = useState(false);
  const [yieldQty, setYieldQty] = useState(wo.quantity);
  const [scrapQty, setScrapQty] = useState(0);
  const [duration, setDuration] = useState(0);
  const [completionNotes, setCompletionNotes] = useState('');

  const handleStart = () => start.mutate(wo.serial_number);
  const handleCancel = () => cancel.mutate(wo.serial_number);

  const handleComplete = (e: React.FormEvent) => {
    e.preventDefault();
    const body: CompleteWorkOrderPayload = {
      yield_quantity: yieldQty,
      scrap_quantity: scrapQty,
      duration_minutes: duration || undefined,
      notes: completionNotes || undefined,
    };
    complete.mutate(
      { serial: wo.serial_number, body },
      { onSuccess: () => setShowComplete(false) },
    );
  };

  return (
    <div className="pac-glass-raised rounded p-4 space-y-3 border border-neutral-800">
      {/* Summary */}
      <div className="flex items-center gap-3">
        <StatusBadge status={wo.status} />
        <span className="text-sm text-neutral-300">{wo.label}</span>
      </div>

      {/* SKU + BOM */}
      <div className="text-xs text-neutral-400 space-y-0.5">
        <p>Product: <span className="text-purple-400 font-mono">{wo.finished_sku}</span></p>
        <p>BOM: <span className="text-neutral-300 font-mono">{wo.bom_key}</span></p>
        <p>Quantity: <span className="text-neutral-200">{wo.quantity}</span></p>
      </div>

      {/* Component requirements */}
      {wo.components.length > 0 && (
        <div>
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
            Component Requirements
          </p>
          <div className="space-y-1">
            {wo.components.map((c, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <span className="text-emerald-400 w-8 text-right font-mono">{c.required_qty}×</span>
                <span className="font-mono text-neutral-300">{c.sku}</span>
                <span className="text-neutral-500">({c.unit})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion metrics (if completed) */}
      {wo.status === 'completed' && (
        <div className="pac-glass-subtle rounded p-3 border border-emerald-900/30 text-xs text-neutral-300 space-y-1">
          <p>Yield: <span className="text-emerald-400 font-mono">{wo.yield_quantity}</span> units</p>
          {(wo.scrap_quantity ?? 0) > 0 && (
            <p>Scrap: <span className="text-rose-400 font-mono">{wo.scrap_quantity}</span> units</p>
          )}
          {wo.duration_minutes != null && (
            <p>Duration: <span className="text-neutral-200">{wo.duration_minutes} min</span></p>
          )}
          {wo.started_at && <p>Started: {wo.started_at}</p>}
          {wo.completed_at && <p>Completed: {wo.completed_at}</p>}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        {wo.status === 'draft' && (
          <>
            <button
              onClick={handleStart}
              disabled={start.isPending}
              className="pac-glass-btn px-3 py-1.5 rounded text-xs border border-blue-500/40
                         text-blue-400 hover:border-blue-400 transition-colors
                         disabled:opacity-40"
            >
              {start.isPending ? 'Starting…' : 'Start Production'}
            </button>
            <button
              onClick={handleCancel}
              disabled={cancel.isPending}
              className="pac-glass-btn px-3 py-1.5 rounded text-xs border border-rose-500/40
                         text-rose-400 hover:border-rose-400 transition-colors
                         disabled:opacity-40"
            >
              Cancel
            </button>
          </>
        )}

        {wo.status === 'in_progress' && (
          <>
            <button
              onClick={() => setShowComplete(c => !c)}
              className="pac-glass-btn px-3 py-1.5 rounded text-xs border border-emerald-500/40
                         text-emerald-400 hover:border-emerald-400 transition-colors"
            >
              Complete
            </button>
            <button
              onClick={handleCancel}
              disabled={cancel.isPending}
              className="pac-glass-btn px-3 py-1.5 rounded text-xs border border-rose-500/40
                         text-rose-400 hover:border-rose-400 transition-colors
                         disabled:opacity-40"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* ── Completion form ────────────────────────────────────────── */}
      {showComplete && wo.status === 'in_progress' && (
        <form onSubmit={handleComplete} className="pac-glass-subtle rounded p-3 space-y-3 border border-white/5">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Record Completion
          </p>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] text-neutral-500 mb-0.5">Yield</label>
              <input
                type="number"
                value={yieldQty}
                onChange={e => setYieldQty(Number(e.target.value))}
                min={0}
                className="pac-glass-input w-full rounded px-2 py-1.5 text-xs text-neutral-200
                           bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                           outline-none text-center"
              />
            </div>
            <div>
              <label className="block text-[10px] text-neutral-500 mb-0.5">Scrap</label>
              <input
                type="number"
                value={scrapQty}
                onChange={e => setScrapQty(Number(e.target.value))}
                min={0}
                className="pac-glass-input w-full rounded px-2 py-1.5 text-xs text-neutral-200
                           bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                           outline-none text-center"
              />
            </div>
            <div>
              <label className="block text-[10px] text-neutral-500 mb-0.5">Duration (min)</label>
              <input
                type="number"
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                min={0}
                className="pac-glass-input w-full rounded px-2 py-1.5 text-xs text-neutral-200
                           bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                           outline-none text-center"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] text-neutral-500 mb-0.5">Notes</label>
            <input
              type="text"
              value={completionNotes}
              onChange={e => setCompletionNotes(e.target.value)}
              placeholder="QC pass, defects, etc."
              className="pac-glass-input w-full rounded px-2 py-1.5 text-xs text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={complete.isPending}
            className="pac-glass-btn px-3 py-1.5 rounded text-xs border border-emerald-500/40
                       text-emerald-400 hover:border-emerald-400 transition-colors
                       disabled:opacity-40"
          >
            {complete.isPending ? 'Saving…' : 'Confirm Complete'}
          </button>
        </form>
      )}

      {/* Notes */}
      {wo.notes && (
        <p className="text-[10px] text-neutral-500 italic">{wo.notes}</p>
      )}
    </div>
  );
}

// ── Main list ────────────────────────────────────────────────────────────────

export default function WorkOrderList() {
  const { data, isLoading, isError, error } = useProductionOrders({ limit: 50 });
  const [selected, setSelected] = useState<string | null>(null);

  const toggle = useCallback(
    (s: string) => setSelected(prev => (prev === s ? null : s)),
    [],
  );

  const orders: WorkOrder[] = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500 animate-pulse">Loading work orders…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-rose-400">{(error as Error)?.message ?? 'Failed to load work orders'}</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500">No work orders yet.</p>
      </div>
    );
  }

  return (
    <div className="pac-glass rounded-lg divide-y divide-white/5">
      {orders.map(wo => (
        <div key={wo.serial_number}>
          <button
            onClick={() => toggle(wo.serial_number)}
            className="w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-white/[0.03] transition-colors"
          >
            <span className="font-mono text-sm text-neutral-200 w-44 shrink-0">
              {wo.serial_number}
            </span>
            <StatusBadge status={wo.status} />
            <span className="text-sm text-neutral-400 truncate flex-1">
              {wo.label}
            </span>
            <span className="text-xs text-neutral-600">
              {wo.finished_sku} · x{wo.quantity}
            </span>
          </button>

          {selected === wo.serial_number && (
            <div className="px-5 pb-4">
              <WorkOrderDetail wo={wo} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
